import "./App.css";
import "./g6th-sense-theme.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ChatWidget from "./components/ChatWidget.jsx";
import EmbedDemo from "./components/EmbedDemo.jsx";
import WidgetPreview from "./pages/WidgetPreview.jsx";
import VisitorDetail from "./components/VisitorDetail.jsx";
import AdminLayout from "./components/AdminLayout.jsx";
import {
  DashboardPage,
  VisitorsPage,
  TrafficPage,
  ChatsPage,
  KnowledgePage,
  FeedbackPage,
  SettingsPage,
  AgentsPage,
  MaintenancePage,
  PersonasPage,
  AccountPage,
  OfflineMessagesPage
} from "./pages/admin";

// Auth components
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import SuperAdminLogin from "./pages/SuperAdminLogin";
import SuperAdminSignup from "./pages/SuperAdminSignup";
import SuperAdminPage from "./pages/SuperAdminPage";
import { logger } from "./utils/logger";

// Determine if this is super admin domain based on hostname or env variable
const isSuperAdminDomain = () => {
  const hostname = window.location.hostname;
  const mode = process.env.REACT_APP_MODE;

  logger.log('[App] REACT_APP_MODE:', mode);
  logger.log('[App] hostname:', hostname);

  // Check for super admin subdomain or explicit mode
  const isSuperAdmin =
    hostname === 'admin.g6thsense.com' ||           // Production super admin
    hostname === 'g6thsenseadmin.test.gate6.com' || // Test/staging super admin
    hostname.startsWith('admin.') ||                 // Any admin. subdomain
    mode === 'superadmin';                           // Explicit mode from env

  logger.log('[App] isSuperAdmin:', isSuperAdmin);
  return isSuperAdmin;
};

function App() {
  const superAdminMode = isSuperAdminDomain();

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {superAdminMode ? (
              // Super Admin Domain Routes - Only show super admin functionality
              <>
                <Route path="/" element={<SuperAdminLogin />} />
                <Route path="/login" element={<SuperAdminLogin />} />
                <Route path="/signup" element={<SuperAdminSignup />} />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <SuperAdminPage />
                    </ProtectedRoute>
                  }
                />
                {/* Catch all - redirect to login */}
                <Route path="*" element={<Navigate to="/login" replace />} />
              </>
            ) : (
              // Tenant Domain Routes - Login is landing page
              <>
                {/* Auth Routes - Login is now the landing page */}
                <Route path="/" element={<Login />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />

                {/* Demo page - Shows after login with working widget and Admin Panel button */}
                <Route
                  path="/demo"
                  element={
                    <ProtectedRoute>
                      <EmbedDemo />
                    </ProtectedRoute>
                  }
                />

                {/* Widget Preview - Shows embed code */}
                <Route
                  path="/widget-preview"
                  element={
                    <ProtectedRoute>
                      <WidgetPreview />
                    </ProtectedRoute>
                  }
                />

                {/* Widget iframe endpoint - Used by embedded widget */}
                <Route path="/widget" element={<ChatWidget embedded={false} />} />

                {/* Protected Admin Routes with Layout */}
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <AdminLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<DashboardPage />} />
                  <Route path="dashboard" element={<DashboardPage />} />
                  <Route path="visitors" element={<VisitorsPage />} />
                  <Route path="traffic" element={<TrafficPage />} />
                  <Route path="chats" element={<ChatsPage />} />
                  <Route path="knowledge" element={<KnowledgePage />} />
                  <Route path="feedback" element={<FeedbackPage />} />
                  <Route path="settings" element={<SettingsPage />} />
                  <Route path="agents" element={<AgentsPage />} />
                  <Route path="maintenance" element={<MaintenancePage />} />
                  <Route path="personas" element={<PersonasPage />} />
                  <Route path="account" element={<AccountPage />} />
                  <Route path="offline-messages" element={<OfflineMessagesPage />} />
                  <Route path="visitor/:sessionId" element={<VisitorDetail />} />
                </Route>

                {/* Redirect /dashboard to /admin for convenience */}
                <Route path="/dashboard" element={<Navigate to="/admin" replace />} />

                {/* Catch all - redirect to login */}
                <Route path="*" element={<Navigate to="/login" replace />} />
              </>
            )}
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
